import React from 'react';
import {
  IconCollection,
  IconArrowUp,
  IconDownload,
  IconHide,
} from '@bynder/icons';
import styled, { css } from 'styled-components';
import {
  Button,
  Flex,
  SkeletonImage,
  SkeletonList,
  token,
} from '@bynder/design-system';
import { Translate, useTranslate } from '@bynder/localization';
import { API_ROUTES } from '../../../constants/app';
import { WidgetPosition } from '../../../constants/widgets';
import {
  getTypography,
  getTypographyWeight,
} from '../../shared/styled-components/constants';
import { TypographyHeading } from '../../../types/typography';
import { useGetCollection } from '../../../queries/collections';
import DownloadCollectionButton from './DownloadCollectionButton';
import { CollectionCard } from './CollectionCard';
import type { CollectionWidget } from '../../../types/widgets/collections';
import { CollectionLayoutOptions } from '../../../types/widgets/collections';
import { CSS_CLASSES, CSS_PROPERTIES } from '../../guides/Inspector/constants';
import {
  addQuotes,
  removeDot,
  toPx,
  useWidgetStyleProperty,
} from '../../guides/Inspector/helpers';

interface Props {
  widget: CollectionWidget;
  readOnly: boolean;
  collectionId: string;
  widgetPosition: WidgetPosition;
  numberOfColumns: number;
}

const CollectionWidget: React.FC<Props> = ({
  widget,
  readOnly,
  collectionId,
  widgetPosition,
  numberOfColumns,
}: Props) => {
  const { data, isError, isLoading } = useGetCollection(collectionId);
  const isEmpty = !collectionId;
  const [showAllAssets, setShowAllAssets] = React.useState(false);

  const { translate } = useTranslate();
  const layout =
    widget?.collectionSettings?.layout || CollectionLayoutOptions.MASONRY;
  const assetsShown = widget?.collectionSettings?.assetsShown || 4;
  const showAssetTitle = widget?.collectionSettings?.showAssetTitle;
  const showAssetType = widget?.collectionSettings?.showAssetType;
  const showAssetDownload = widget?.collectionSettings?.showAssetDownload;
  const showOpenAssetInAssetBank =
    widget?.collectionSettings?.showOpenAssetInAssetBank;
  const showCollectionDownload =
    widget?.collectionSettings?.showCollectionDownload;
  const allowAssetRedirection =
    widget?.collectionSettings?.allowAssetRedirection;
  const allowCollectionRedirection =
    widget?.collectionSettings?.allowCollectionRedirection;

  const collectionUrl = React.useMemo(() => {
    try {
      // Sanitize the collection ID to prevent XSS
      const safeCollectionId = data?.collectionId
        ? encodeURIComponent(data.collectionId)
        : '';
      return new URL(
        `/web/${safeCollectionId}`,
        `https://${location.hostname}`,
      ).toString();
    } catch (e) {
      return '#';
    }
  }, [data?.collectionId]);

  const { value: titleColor } = useWidgetStyleProperty(
    widget?.style.stylesheet,
    'widgets.collection.collectionTitle.textColor.hexCode',
    CSS_PROPERTIES.COLOR,
    CSS_CLASSES.COLLECTION_TITLE,
  ) as { value: string; origin?: string };

  const { value: titleFontFamily } = useWidgetStyleProperty(
    widget?.style.stylesheet,
    'widgets.collection.collectionTitle.fontFamily',
    CSS_PROPERTIES.FONT_FAMILY,
    CSS_CLASSES.COLLECTION_TITLE,
  );
  const { value: titleFontSize } = useWidgetStyleProperty(
    widget?.style.stylesheet,
    'widgets.collection.collectionTitle.fontSize',
    CSS_PROPERTIES.FONT_SIZE,
    CSS_CLASSES.COLLECTION_TITLE,
  );
  const { value: titleFontStyle } = useWidgetStyleProperty(
    widget?.style.stylesheet,
    'widgets.collection.collectionTitle.fontStyle',
    CSS_PROPERTIES.FONT_WEIGHT,
    CSS_CLASSES.COLLECTION_TITLE,
  );

  const { value: assetNameColor } = useWidgetStyleProperty(
    widget?.style.stylesheet,
    'widgets.collection.assetTitle.textColor.hexCode',
    CSS_PROPERTIES.COLOR,
    CSS_CLASSES.COLLECTION_ASSET,
  );

  const { value: assetNameFontFamily } = useWidgetStyleProperty(
    widget?.style.stylesheet,
    'widgets.collection.assetTitle.fontFamily',
    CSS_PROPERTIES.FONT_FAMILY,
    CSS_CLASSES.COLLECTION_ASSET,
  );

  const { value: assetNameFontSize } = useWidgetStyleProperty(
    widget?.style.stylesheet,
    'widgets.collection.assetTitle.fontSize',
    CSS_PROPERTIES.FONT_SIZE,
    CSS_CLASSES.COLLECTION_ASSET,
  );

  const { value: assetNameFontStyle } = useWidgetStyleProperty(
    widget?.style.stylesheet,
    'widgets.collection.assetTitle.fontStyle',
    CSS_PROPERTIES.FONT_WEIGHT,
    CSS_CLASSES.COLLECTION_ASSET,
  );

  const { value: countNameColor } = useWidgetStyleProperty(
    widget?.style.stylesheet,
    'widgets.collection.count.textColor.hexCode',
    CSS_PROPERTIES.COLOR,
    CSS_CLASSES.COLLECTION_ASSET_COUNT,
  );

  const { value: countNameFontFamily } = useWidgetStyleProperty(
    widget?.style.stylesheet,
    'widgets.collection.count.fontFamily',
    CSS_PROPERTIES.FONT_FAMILY,
    CSS_CLASSES.COLLECTION_ASSET_COUNT,
  );

  const { value: countNameFontSize } = useWidgetStyleProperty(
    widget?.style.stylesheet,
    'widgets.collection.count.fontSize',
    CSS_PROPERTIES.FONT_SIZE,
    CSS_CLASSES.COLLECTION_ASSET_COUNT,
  );

  const { value: countNameFontStyle } = useWidgetStyleProperty(
    widget?.style.stylesheet,
    'widgets.collection.count.fontStyle',
    CSS_PROPERTIES.FONT_WEIGHT,
    CSS_CLASSES.COLLECTION_ASSET_COUNT,
  );

  const { value: borderRadius } = useWidgetStyleProperty(
    widget?.style.stylesheet,
    'widgets.card.borderRadius',
    CSS_PROPERTIES.BORDER_RADIUS,
  );

  if (isLoading) {
    return <CollectionHeaderSkeleton widgetPosition={widgetPosition} />;
  }

  if (isError) {
    return (
      <PrivateCollectionWarning
        collectionId={collectionId}
        readOnly={readOnly}
      />
    );
  }

  if (isEmpty) {
    return (
      <StyledCollectionWidget stylesheet={widget?.style.stylesheet} isEmpty>
        <StyledContainer>
          <IconCollection />
        </StyledContainer>
      </StyledCollectionWidget>
    );
  }

  return (
    <StyledCollectionWidget
      stylesheet={widget?.style.stylesheet}
      data-testid="collection-widget"
    >
      <StyledCollectionHeader>
        <Flex alignItems="center" justifyContent="space-between">
          <StyledCollectionHeaderContainer
            className={removeDot(CSS_CLASSES.COLLECTION_TITLE)}
            $collectionTitle={{
              fontFamily: titleFontFamily,
              fontStyle: titleFontStyle,
              fontSize: parseInt(titleFontSize),
              textColor: titleColor,
            }}
          >
            {allowCollectionRedirection ? (
              <a href={collectionUrl} target="_blank" rel="noreferrer">
                {data.collectionName}
              </a>
            ) : (
              data.collectionName
            )}
            <br />
            <AssetCount
              className={removeDot(CSS_CLASSES.COLLECTION_ASSET_COUNT)}
              $count={{
                fontFamily: countNameFontFamily,
                fontStyle: countNameFontStyle,
                fontSize: parseInt(countNameFontSize, 10),
                textColor: countNameColor,
              }}
            >
              <Translate
                id="WIDGETS_CONSTANTS.COLLECTION_ASSET_COUNT"
                count={data.collectionAssetCount}
              />
            </AssetCount>
          </StyledCollectionHeaderContainer>
          {readOnly && showCollectionDownload && (
            <DownloadCollectionButton
              numberOfColumns={numberOfColumns}
              collection={data}
            />
          )}
        </Flex>
      </StyledCollectionHeader>
      <CollectionLayoutContainer
        layout={layout}
        widgetPosition={widgetPosition}
      >
        {data.collectionPreviews.map((asset, index, collection) => {
          if (showAllAssets)
            return (
              <CollectionCard
                $borderRadius={borderRadius}
                assetNameColor={assetNameColor}
                layout={layout}
                showOpenAssetInAssetBank={showOpenAssetInAssetBank}
                allowAssetRedirection={allowAssetRedirection}
                showAssetDownload={showAssetDownload}
                showAssetTitle={showAssetTitle}
                showAssetType={showAssetType}
                preview={asset}
                key={asset.assetId}
                collectionId={data.collectionId}
                $assetTitle={{
                  fontFamily: assetNameFontFamily,
                  fontSize: assetNameFontSize,
                  fontStyle: assetNameFontStyle,
                  textColor: assetNameColor,
                }}
              />
            );

          if (index < assetsShown - 1) {
            return (
              <CollectionCard
                $borderRadius={borderRadius}
                assetNameColor={assetNameColor}
                layout={layout}
                showOpenAssetInAssetBank={showOpenAssetInAssetBank}
                allowAssetRedirection={allowAssetRedirection}
                showAssetDownload={showAssetDownload}
                showAssetTitle={showAssetTitle}
                showAssetType={showAssetType}
                preview={asset}
                key={asset.assetId}
                collectionId={data.collectionId}
                $assetTitle={{
                  fontFamily: assetNameFontFamily,
                  fontSize: assetNameFontSize,
                  fontStyle: assetNameFontStyle,
                  textColor: assetNameColor,
                }}
              />
            );
          } else if (index === assetsShown - 1)
            if (collection.length > assetsShown) {
              return (
                <CollectionCard
                  $borderRadius={borderRadius}
                  assetNameColor={assetNameColor}
                  layout={layout}
                  showOpenAssetInAssetBank={showOpenAssetInAssetBank}
                  allowAssetRedirection={allowAssetRedirection}
                  showAssetDownload={showAssetDownload}
                  showAssetTitle={showAssetTitle}
                  showAssetType={showAssetType}
                  key={asset.assetId}
                  preview={asset}
                  onClick={() => setShowAllAssets(true)}
                  isLast
                  remainingAssets={collection.length - 1 - index}
                />
              );
            } else {
              return (
                <CollectionCard
                  $borderRadius={borderRadius}
                  assetNameColor={assetNameColor}
                  layout={layout}
                  showOpenAssetInAssetBank={showOpenAssetInAssetBank}
                  allowAssetRedirection={allowAssetRedirection}
                  showAssetDownload={showAssetDownload}
                  showAssetTitle={showAssetTitle}
                  showAssetType={showAssetType}
                  preview={asset}
                  key={asset.assetId}
                  collectionId={data.collectionId}
                  $assetTitle={{
                    fontFamily: assetNameFontFamily,
                    fontSize: assetNameFontSize,
                    fontStyle: assetNameFontStyle,
                    textColor: assetNameColor,
                  }}
                />
              );
            }

          return null;
        })}
      </CollectionLayoutContainer>
      {showAllAssets && (
        <StyledFlex justifyContent="flex-end">
          <Button
            variant="clean"
            icon={<IconArrowUp />}
            onClick={(e: React.SyntheticEvent) => {
              e.stopPropagation();
              setShowAllAssets(!showAllAssets);
            }}
          >
            {translate('WIDGETS_CONSTANTS.COLLECTION_MINIMIZE')}
          </Button>
        </StyledFlex>
      )}
    </StyledCollectionWidget>
  );
};

export default CollectionWidget;

const StyledFlex = styled(Flex)`
  margin-top: ${token.spacing4};
`;

export const StyledCollectionWidget = styled.div<{
  isEmpty?: boolean;
  stylesheet: string;
}>`
  width: 100%;
  background-color: transparent;
  ${props =>
    props.isEmpty &&
    css`
      border: 1px dashed ${token.gray200a};
      justify-items: center;
      flex-direction: row;
      align-items: center;
    `};

  && {
    ${props => props.stylesheet}
  }
`;

export const StyledCollectionHeader = styled.div`
  overflow: hidden;
  overflow-wrap: anywhere;
  white-space: break-spaces;
  text-overflow: ellipsis;
  text-align: left;
  ${props => getTypography(TypographyHeading.NORMAL, props.theme, false)};
  padding: 0;
  margin-bottom: ${token.spacing5};
  font-size: ${token.fontSize400};
  line-height: 28px;
`;

export const StyledCollectionHeaderContainer = styled.div<{
  $collectionTitle?: {
    fontFamily: string;
    fontStyle: string;
    fontSize: number;
    textColor: string;
  };
}>`
  position: relative;
  a {
    color: ${props =>
      props.$collectionTitle?.textColor && props.$collectionTitle?.textColor};
    &:hover {
      color: ${props =>
        props.$collectionTitle?.textColor && props.$collectionTitle?.textColor};
    }
    font-family: ${props =>
      props.$collectionTitle?.fontFamily &&
      addQuotes(props.$collectionTitle?.fontFamily)};
    font-size: ${props =>
      props.$collectionTitle?.fontSize && props.$collectionTitle?.fontSize}px;
    font-weight: ${props =>
      props.$collectionTitle?.fontStyle &&
      getTypographyWeight(
        props.theme,
        props.$collectionTitle?.fontFamily,
        props.$collectionTitle?.fontStyle,
      )};
  }
`;

export const AssetCount = styled.small<{
  $count?: {
    fontFamily: string;
    fontStyle: string;
    fontSize: number;
    textColor: string;
  };
}>`
  color: ${props => props.$count?.textColor && props.$count?.textColor};
  font-family: ${props =>
    props.$count?.fontFamily && addQuotes(props.$count?.fontFamily)};
  font-size: ${props => props.$count?.fontSize && toPx(props.$count?.fontSize)};
  font-weight: ${props =>
    props.$count?.fontStyle &&
    getTypographyWeight(
      props.theme,
      props.$count?.fontFamily,
      props.$count?.fontStyle,
    )};
`;

const StyledContainer = styled.div`
  border-radius: ${token.radiusLarge};
  margin: 4px;
  cursor: pointer;
  min-height: 236px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  svg {
    color: ${token.gray500a};
  }
  &:hover {
    background-color: ${token.gray25a};
    svg {
      color: ${token.colorText};
    }
  }
`;

interface SkeletonProps {
  widgetPosition: WidgetPosition;
}
const CollectionHeaderSkeleton: React.FC<SkeletonProps> = ({
  widgetPosition,
}) => {
  return (
    <Flex direction="column">
      <StyledCollectionSkeletonContainer $widgetPosition={widgetPosition}>
        <SkeletonList
          amount={1}
          itemRender={<SkeletonList.Item isSubtextShow />}
        />
        <Button isDisabled icon={<IconDownload />}>
          {widgetPosition === WidgetPosition.ONE_COLUMN && (
            <Translate id="WIDGETS_CONSTANTS.DOWNLOAD_COLLECTION" />
          )}
        </Button>
      </StyledCollectionSkeletonContainer>
      <Grid $widgetPosition={widgetPosition}>
        {Array.from(Array(4).keys()).map(key => (
          <SkeletonImage width="256px" height="160px" key={key} />
        ))}
      </Grid>
    </Flex>
  );
};

const StyledCollectionSkeletonContainer = styled.div<{
  $widgetPosition: WidgetPosition;
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

interface PrivateCollectionProps {
  readOnly: boolean;
  collectionId: string;
}

const PrivateCollectionWarning: React.FC<PrivateCollectionProps> = ({
  readOnly,
  collectionId,
}) => {
  const { translate } = useTranslate();

  const handleButtonClick = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    window.open(API_ROUTES.COLLECTION_DETAIL(collectionId), '_blank');
  };

  return (
    <StyledWarningContainer readOnly={readOnly}>
      <StyledWarningIconContainer>
        <IconHide style={{ color: token.gray400 }} size="48px" />
      </StyledWarningIconContainer>
      <StyledWarningTitle>
        {translate('WIDGETS_CONSTANTS.PRIVATE_COLLECTION_TITLE')}
      </StyledWarningTitle>
      <StyledWarningSubtitle>
        {translate('WIDGETS_CONSTANTS.PRIVATE_COLLECTION_SUBTITLE')}
      </StyledWarningSubtitle>
      <Button variant="primary" onClick={e => handleButtonClick(e)}>
        {translate('WIDGETS_CONSTANTS.PRIVATE_COLLECTION_BUTTON')}
      </Button>
    </StyledWarningContainer>
  );
};

const StyledWarningIconContainer = styled.div`
  height: 128px;
  width: 128px;
  background: ${token.gray25};
  border-radius: ${token.radiusCircle};
  display: flex;
  justify-content: center;
  align-items: center;
`;
const StyledWarningContainer = styled.div<{ readOnly?: boolean }>`
  width: 100%;
  border-radius: ${token.radiusLarge};
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  padding: ${token.spacing7} 0px ${token.spacing5};
  border: 1px solid ${token.gray100};
  ${props =>
    !props.readOnly &&
    css`
      cursor: pointer;
    `};
`;

const StyledWarningTitle = styled.div`
  margin-top: ${token.spacing5};
  color: ${token.gray800};
  font-size: ${token.fontSize400};
`;

const StyledWarningSubtitle = styled.div`
  margin-top: ${token.spacing4};
  margin-bottom: ${token.spacing6};
  color: ${token.gray600};
  font-size: ${token.fontSize100};
`;

const Grid = styled.div<{
  $widgetPosition: WidgetPosition;
}>`
  display: grid;
  ${({ $widgetPosition }) => {
    switch ($widgetPosition) {
      case WidgetPosition.TWO_COLUMNS_EQUAL:
      case WidgetPosition.TWO_COLUMNS_SMALL:
      case WidgetPosition.THREE_COLUMNS_EQUAL:
        return `grid-template-columns: repeat(auto-fill, 120px);`;
      case WidgetPosition.TWO_COLUMNS_LARGE:
        return `grid-template-columns: repeat(auto-fill, 192px);`;
      default:
        return `grid-template-columns: repeat(auto-fill, 192px);`;
    }
  }}
  grid-column-gap: 8px;
  grid-row-gap: 8px;
  align-items: flex-start;
  div {
    min-height: unset;
    min-width: 0px;
    ${({ $widgetPosition }) => {
      switch ($widgetPosition) {
        case WidgetPosition.THREE_COLUMNS_EQUAL:
          return `max-width: 120px;`;
        default:
          return `max-width: 192px;`;
      }
    }}
  }
  span {
    ${({ $widgetPosition }) => {
      switch ($widgetPosition) {
        case WidgetPosition.THREE_COLUMNS_EQUAL:
          return `max-width: 120px;`;
        default:
          return `max-width: 192px;`;
      }
    }}
  }
`;

const StyledMasonry = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(224px, 1fr));
  gap: ${token.spacing4};
  align-items: flex-start;
`;

export const CollectionLayoutContainer: React.FC<{
  widgetPosition: WidgetPosition;
  layout: CollectionLayoutOptions;
  children?: React.ReactNode;
}> = ({ children, widgetPosition, layout }) => {
  return layout === CollectionLayoutOptions.MASONRY ? (
    <StyledMasonry>{children}</StyledMasonry>
  ) : (
    <Grid $widgetPosition={widgetPosition}>{children}</Grid>
  );
};
